import * as React from 'react'

import { usePartnerProfile } from './hooks'
import {
  Avatar,
  Button,
  Flex,
  notification,
  Popconfirm,
  Popover,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  CopyOutlined,
  ExperimentOutlined,
  RiseOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { Partner } from './types'
import { path } from 'src/libs'
import { PartnerClientsWidget } from '../widgets/partnerClients'
import { submitEditPartner, submitChangeStatus } from './model'
import { EditOutlined } from '@ant-design/icons'
import { Guard } from '../../../../features/session/guard'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const { Text } = Typography

// ── Helpers ────────────────────────────────────────────────

const AVATAR_COLORS = ['#3b82f6', '#a855f7', '#06b6d4', '#22c55e', '#f59e0b']
const avatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]

const LEVEL_COLOR: Record<string, string> = {
  bronze: 'gold',
  silver: 'default',
  gold: '#f59e0b',
  royal: '#a855f7',
}

function InfoRow({
  label,
  value,
  last = false,
}: {
  label: string
  value: React.ReactNode
  last?: boolean
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '5px 0',
        borderBottom: last
          ? 'none'
          : '1px solid var(--crm-border, #e2e5ec)',
      }}
    >
      <Text
        type="secondary"
        style={{ fontSize: 11, flexShrink: 0, marginRight: 8 }}
      >
        {label}
      </Text>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'monospace',
          textAlign: 'right',
          maxWidth: '65%',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  )
}

function CommissionRow({
  label,
  value,
  dotColor,
  inDev = false,
  ticketUrl,
}: {
  label: string
  value?: React.ReactNode
  dotColor: string
  inDev?: boolean
  ticketUrl?: string
}) {
  return (
    <Flex
      justify="space-between"
      align="center"
      style={{
        padding: '7px 0',
        borderBottom: '1px solid var(--crm-border, #e2e5ec)',
      }}
    >
      <Flex align="center" gap={8}>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
          }}
        />
        <Text style={{ fontSize: 12 }}>{label}</Text>
      </Flex>
      {inDev ? (
        <Flex align="center" gap={6}>
          <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>
            In development
          </Tag>
          {ticketUrl && (
            <a
              href={ticketUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 10, color: '#3b82f6' }}
            >
              HW-1543 ↗
            </a>
          )}
        </Flex>
      ) : (
        <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>
          {value}
        </Text>
      )}
    </Flex>
  )
}

const cardStyle: React.CSSProperties = {
  background: 'var(--crm-bg, #ffffff)',
  border: '1px solid var(--crm-border, #e2e5ec)',
  borderRadius: 8,
  padding: '14px 16px',
  marginBottom: 12,
}

const sectionTitle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--crm-text-secondary, #9ca3af)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 10,
}

// ── Profile component ──────────────────────────────────────

const PartnerProfileCard = ({
  partner,
  clientPartner,
}: {
  partner: Partner
  clientPartner: { id: string; firstName: string }
}) => {
  const fullName = partner.client?.fullName || partner.fullName || '—'
  const email = partner.client?.email || partner.email || ''
  const isTest = partner.client?.isTest || false
  const isPremium = partner.client?.isPremium || false

  const handleCopyInfo = () => {
    const origin = window.location.origin
    const partnerUrl = `${origin}${path.ib.partners.details(partner.id)}`
    const plainText = `Name: ${fullName}\nEmail: ${email}\nClient ID: ${partner.id}`
    const htmlText = `<b>Name:</b> ${fullName}<br><b>Email:</b> ${email}<br><b>Client ID:</b> <a href="${partnerUrl}">${partner.id}</a>`
    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      }),
    ])
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 300px',
        gap: 16,
        alignItems: 'start',
      }}
    >
      {/* LEFT */}
      <div>
        {/* Header card */}
        <div
          style={{
            ...cardStyle,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            marginBottom: 14,
          }}
        >
          <Avatar
            size={46}
            style={{
              background: avatarColor(fullName),
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {fullName.slice(0, 2).toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Flex
              align="center"
              gap={8}
              wrap="wrap"
              style={{ marginBottom: 4 }}
            >
              <Text
                strong
                style={{ fontSize: 14, fontFamily: 'monospace' }}
              >
                {fullName}
              </Text>
              {isTest && (
                <Tag
                  icon={<ExperimentOutlined />}
                  color="purple"
                  style={{ fontSize: 10, margin: 0 }}
                >
                  Test
                </Tag>
              )}
              {isPremium && (
                <Tag
                  color="gold"
                  style={{ fontSize: 10, margin: 0 }}
                >
                  Premium
                </Tag>
              )}
              <Tag style={{ fontSize: 10, margin: 0 }}>#{partner.id}</Tag>
              {partner.status && (
                <Tag
                  color={LEVEL_COLOR[partner.status] || 'default'}
                  style={{
                    fontSize: 10,
                    margin: 0,
                    textTransform: 'capitalize',
                  }}
                >
                  {partner.status}
                </Tag>
              )}
            </Flex>
            <Text
              type="secondary"
              style={{ fontSize: 11, fontFamily: 'monospace' }}
            >
              {email}
            </Text>
            <Flex gap={16} style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Clients: <Text strong>{partner.activeClients}</Text>
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Lots: <Text strong>{partner.sumLots}</Text>
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Partner:{' '}
                {clientPartner?.id ? (
                  <a href={path.ib.partners.details(clientPartner.id)}>
                    {clientPartner.firstName} (#{clientPartner.id})
                  </a>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Text>
            </Flex>
          </div>
          <Tooltip title="Copy name + email + client ID">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopyInfo}
              style={{ flexShrink: 0 }}
            >
              Copy info
            </Button>
          </Tooltip>
        </div>

        {/* Stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 10,
            marginBottom: 14,
          }}
        >
          {[
            {
              label: 'Active clients',
              value: partner.activeClients,
              sub: 'with deposit',
              icon: <TeamOutlined />,
              color: '#3b82f6',
            },
            {
              label: 'All clients',
              value: partner.partnerClientsCount,
              sub: 'attached',
              icon: <TeamOutlined />,
              color: '#06b6d4',
            },
            {
              label: 'Lots',
              value: partner.sumLots,
              sub: 'total traded',
              icon: <RiseOutlined />,
              color: '#a855f7',
            },
            {
              label: 'IB Balance',
              value: partner.withdrawalAmount,
              sub: 'USD',
              icon: <WalletOutlined />,
              color: '#22c55e',
            },
          ].map((s, i) => (
            <div key={i} style={{ ...cardStyle, marginBottom: 0 }}>
              <div style={sectionTitle}>{s.label}</div>
              <Flex align="baseline" gap={6}>
                <span
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: s.color,
                  }}
                >
                  {s.value || '0'}
                </span>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {s.sub}
                </Text>
              </Flex>
            </div>
          ))}
        </div>

        {/* General info */}
        <div style={cardStyle}>
          <div style={sectionTitle}>General information</div>
          <InfoRow label="ID" value={partner.id} />
          <InfoRow label="Key" value={partner.key} />
          <InfoRow label="Email" value={email} />
          <InfoRow
            label="Registration date"
            value={
              partner.registeredAt
                ? dayjs(partner.registeredAt)
                    .utcOffset(3)
                    .format('YYYY-MM-DD HH:mm:ss') + ' GMT+3'
                : undefined
            }
          />
          <InfoRow label="Full name" value={fullName} />
          <InfoRow
            label="Partner"
            value={
              clientPartner?.id ? (
                <a href={path.ib.partners.details(clientPartner.id)}>
                  {clientPartner.firstName} (#{clientPartner.id})
                </a>
              ) : (
                <Text type="secondary">—</Text>
              )
            }
          />
          <InfoRow
            label="Level"
            value={
              partner.status ? (
                <Tag
                  color={LEVEL_COLOR[partner.status] || 'default'}
                  style={{ fontSize: 10, textTransform: 'capitalize' }}
                >
                  {partner.status}
                </Tag>
              ) : undefined
            }
          />
          <InfoRow
            label="Is test"
            value={
              <Tag
                color={isTest ? 'purple' : 'default'}
                style={{ fontSize: 10 }}
              >
                {isTest ? 'Yes' : 'No'}
              </Tag>
            }
          />
          {partner.humanStoppedPaymentCommissionsAt && (
            <InfoRow
              label="Commission stopped at"
              value={partner.humanStoppedPaymentCommissionsAt}
            />
          )}
        </div>

        {/* Commission */}
        <div style={cardStyle}>
          <div style={sectionTitle}>All commission</div>
          <CommissionRow
            label="From trading (Level 1)"
            value={partner.sumCommissions}
            dotColor="#3b82f6"
          />
          <CommissionRow
            label="From partner Level 2"
            value={partner.sumCommissionsLevelTwo ?? undefined}
            inDev={partner.sumCommissionsLevelTwo == null}
            dotColor="#a855f7"
            ticketUrl="https://h2o-team.atlassian.net/browse/HW-1543"
          />
          <CommissionRow
            label="From partner Level 3"
            value={partner.sumCommissionsLevelThree ?? undefined}
            inDev={partner.sumCommissionsLevelThree == null}
            dotColor="#06b6d4"
            ticketUrl="https://h2o-team.atlassian.net/browse/HW-1543"
          />
        </div>

      </div>

      {/* SIDEBAR */}
      <div>
        {/* Partner key */}
        <div style={{ marginBottom: 18 }}>
          <div style={sectionTitle}>Partner key</div>
          <div
            style={{
              background: 'var(--crm-bg-elevated, #f7f8fa)',
              border: '1px solid var(--crm-border, #e2e5ec)',
              borderRadius: 8,
              padding: '12px 14px',
            }}
          >
            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 4 }}
            >
              <Text
                strong
                style={{ fontFamily: 'monospace', fontSize: 14 }}
              >
                {partner.key}
              </Text>
              <Tooltip title="Copy key">
                <CopyOutlined
                  style={{ cursor: 'pointer', color: '#545c6e' }}
                  onClick={() => {
                    navigator.clipboard?.writeText(partner.key)
                  }}
                />
              </Tooltip>
            </Flex>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Unique cookie for client attachment
            </Text>
          </div>
        </div>

        {/* Registration */}
        <div style={{ marginBottom: 18 }}>
          <div style={sectionTitle}>Registration</div>
          <div
            style={{
              background: 'var(--crm-bg-elevated, #f7f8fa)',
              border: '1px solid var(--crm-border, #e2e5ec)',
              borderRadius: 8,
              padding: '12px 14px',
            }}
          >
            <Text
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                display: 'block',
                lineHeight: 1.8,
              }}
            >
              <Text type="secondary" style={{ fontSize: 10 }}>
                Date:{' '}
              </Text>
              {partner.registeredAt
                ? dayjs(partner.registeredAt)
                    .utcOffset(3)
                    .format('YYYY-MM-DD HH:mm:ss')
                : '—'}
              <br />
              <Text type="secondary" style={{ fontSize: 10 }}>
                Timezone:{' '}
              </Text>
              GMT+3
            </Text>
          </div>
        </div>

        {/* Level */}
        <div>
          <div style={sectionTitle}>Level</div>
          <div
            style={{
              background: 'var(--crm-bg-elevated, #f7f8fa)',
              border: '1px solid var(--crm-border, #e2e5ec)',
              borderRadius: 8,
              padding: '12px 14px',
            }}
          >
            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 10 }}
            >
              <Tag
                color={LEVEL_COLOR[partner.status] || 'default'}
                style={{
                  fontSize: 12,
                  padding: '3px 10px',
                  textTransform: 'capitalize',
                }}
              >
                {partner.status || '—'}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Current level
              </Text>
            </Flex>
            <Flex justify="space-between">
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 10, display: 'block', marginBottom: 2 }}
                >
                  Clients
                </Text>
                <Text strong style={{ fontFamily: 'monospace' }}>
                  {partner.activeClients}
                </Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 10, display: 'block', marginBottom: 2 }}
                >
                  Lots
                </Text>
                <Text strong style={{ fontFamily: 'monospace' }}>
                  {partner.sumLots}
                </Text>
              </div>
            </Flex>
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Main page component ────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'Bronze', value: 'bronze' },
  { label: 'Silver', value: 'silver' },
  { label: 'Gold', value: 'gold' },
  { label: 'Royal', value: 'royal' },
]

export const PartnerDetail = () => {
  const { partnerProfile, clientPartner } = usePartnerProfile()
  const loading = !partnerProfile.id
  const [statusPopoverOpen, setStatusPopoverOpen] = React.useState(false)
  const [pendingStatus, setPendingStatus] = React.useState<string | null>(null)

  const isStopped = partnerProfile.stoppedPaymentCommissionsAt != null

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    )
  }

  return (
    <div>
      {/* Tabs + actions */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <Space>
          <Button
                        icon={<UserOutlined />}
            href={path.clients.details(partnerProfile.id)}
            target="_blank"
          >
            Client card ↗
          </Button>
          <Guard permission="change_status_of_partner">
            <Popover
              trigger="click"
              open={statusPopoverOpen}
              onOpenChange={setStatusPopoverOpen}
              title="Change partner level"
              content={
                <div style={{ width: 220 }}>
                  <Select
                    style={{ width: '100%', marginBottom: 10 }}
                    placeholder="Select level"
                    value={pendingStatus}
                    onChange={setPendingStatus}
                    options={STATUS_OPTIONS}
                  />
                  <Flex justify="end" gap={8}>
                    <Button size="small" onClick={() => { setStatusPopoverOpen(false); setPendingStatus(null) }}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      disabled={!pendingStatus || pendingStatus === partnerProfile.status}
                      onClick={() => {
                        if (pendingStatus) {
                          submitChangeStatus({ id: partnerProfile.id, status: pendingStatus })
                          setStatusPopoverOpen(false)
                          setPendingStatus(null)
                        }
                      }}
                    >
                      Apply
                    </Button>
                  </Flex>
                </div>
              }
            >
              <Button icon={<EditOutlined />}>
                Change status
              </Button>
            </Popover>
          </Guard>
          <Guard permission="stop_partner_commission_calc">
            <Popconfirm
              title={
                isStopped
                  ? 'Start commission calculation?'
                  : 'Stop commission calculation?'
              }
              description={
                isStopped
                  ? 'This will resume all commission calculations for this partner.'
                  : 'This will halt all commission calculations for this partner.'
              }
              okText={isStopped ? 'Start' : 'Stop'}
              okButtonProps={{ danger: !isStopped }}
              cancelText="Cancel"
              onConfirm={() => {
                submitEditPartner({
                  id: partnerProfile.id,
                  isStopped: !isStopped,
                })
              }}
            >
              <Button
                danger={!isStopped}
                type={isStopped ? 'default' : undefined}
                icon={<StopOutlined />}
                style={
                  isStopped
                    ? { borderColor: '#22c55e', color: '#22c55e' }
                    : undefined
                }
              >
                {isStopped
                  ? 'Start payment commission calc'
                  : 'Stop payment commission calc'}
              </Button>
            </Popconfirm>
          </Guard>
        </Space>
      </Flex>

      <Tabs
        size="small"
        defaultActiveKey="detail"
        items={[
          {
            key: 'detail',
            label: 'Detail',
            children: (
              <PartnerProfileCard
                partner={partnerProfile}
                clientPartner={clientPartner}
              />
            ),
          },
          {
            key: 'clients',
            label: 'Clients list',
            children: <PartnerClientsWidget />,
          },
          {
            key: 'accounts',
            label: 'Accounts list',
            children: (
              <div style={cardStyle}>
                <Tag>In development</Tag>
              </div>
            ),
          },
          {
            key: 'partners',
            label: 'Partners list',
            children: (
              <div style={cardStyle}>
                <Tag>In development</Tag>
              </div>
            ),
          },
        ]}
      />
    </div>
  )
}
